/**
 * Lead helper V2 — shared theme, formatting, stage/status config for leads UI.
 * Use across LeadsListViewV2, LeadSidebarV2, LeadsV2, and any lead-related views.
 */

import dayjs from 'dayjs';

// ─── Design tokens (green theme, match BookingsListView) ─────────────────────
export const LEAD_THEME = {
  white:   '#ffffff',
  bg:      '#f5f8f6',
  sidebar: '#ffffff',
  ink:     '#0f1a13',
  ink2:    '#253b2c',
  subtle:  '#526b5c',
  muted:   '#8eaa98',
  border:  '#dce8e1',
  borderL: '#eaf1ec',
  green:   '#0a6b3e',
  greenMd: '#0d8a50',
  greenLt: '#e5f5ec',
  greenSf: 'rgba(10,107,62,0.08)',

  // Table (SmartTable) — same as smart_table_helper for consistent list view
  headerBg: '#f8fbf9',
  bodyBg:   '#fafcfb',

  // Stage pill colors (dot, text, bg)
  new:         { dot: '#3b82f6', text: '#1e40af', bg: '#dbeafe' },
  contacted:   { dot: '#06b6d4', text: '#155e75', bg: '#cffafe' },
  qualified:   { dot: '#8b5cf6', text: '#5b21b6', bg: '#ede9fe' },
  follow_up:   { dot: '#f59e0b', text: '#92400e', bg: '#fef3c7' },
  site_visit:  { dot: '#10b981', text: '#065f46', bg: '#d1fae5' },
  negotiation: { dot: '#f97316', text: '#9a3412', bg: '#ffedd5' },
  closure:     { dot: '#22c55e', text: '#14532d', bg: '#dcfce7' },
  closed_won:  { dot: '#22c55e', text: '#14532d', bg: '#dcfce7' },
  lost:        { dot: '#ef4444', text: '#991b1b', bg: '#fee2e2' },
  junk:        { dot: '#9ca3af', text: '#374151', bg: '#f3f4f6' },
  archived:    { dot: '#6b7280', text: '#374151', bg: '#f3f4f6' },
  deleted:     { dot: '#ef4444', text: '#991b1b', bg: '#fee2e2' },
  unassigned:  { dot: '#f59e0b', text: '#92400e', bg: '#fef3c7' },
  assigned:    { dot: '#3b82f6', text: '#1e40af', bg: '#dbeafe' },
};

// ─── Stage config ───────────────────────────────────────────────────────────
export const getStageConfig = (stage) =>
  LEAD_THEME[(stage || 'new').toLowerCase()] || LEAD_THEME.new;

// ─── Status config (funnel sub-state) ───────────────────────────────────────
export const STATUS_COLORS = {
  assigned:        { dot: '#3b82f6', text: '#1e40af', bg: '#dbeafe' },
  unassigned:      { dot: '#f59e0b', text: '#92400e', bg: '#fef3c7' },
  reassigned:      { dot: '#3b82f6', text: '#1e40af', bg: '#dbeafe' },
  contacted:       { dot: '#06b6d4', text: '#155e75', bg: '#cffafe' },
  follow_up_scheduled: { dot: '#8b5cf6', text: '#5b21b6', bg: '#ede9fe' },
  qualified:       { dot: '#8b5cf6', text: '#5b21b6', bg: '#ede9fe' },
  interested:      { dot: '#10b981', text: '#065f46', bg: '#d1fae5' },
  property_shared: { dot: '#10b981', text: '#065f46', bg: '#d1fae5' },
  property_shortlisted: { dot: '#f97316', text: '#9a3412', bg: '#ffedd5' },
  site_visit_scheduled: { dot: '#10b981', text: '#065f46', bg: '#d1fae5' },
  visited:         { dot: '#22c55e', text: '#14532d', bg: '#dcfce7' },
  shortlisted:     { dot: '#f97316', text: '#9a3412', bg: '#ffedd5' },
  offer_made:      { dot: '#f59e0b', text: '#92400e', bg: '#fef3c7' },
  closed_won:      { dot: '#22c55e', text: '#14532d', bg: '#dcfce7' },
  lost:            { dot: '#ef4444', text: '#991b1b', bg: '#fee2e2' },
  not_interested:  { dot: '#ef4444', text: '#991b1b', bg: '#fee2e2' },
  cold_lead:       { dot: '#9ca3af', text: '#374151', bg: '#f3f4f6' },
  unreachable:     { dot: '#9ca3af', text: '#374151', bg: '#f3f4f6' },
  no_response:     { dot: '#9ca3af', text: '#374151', bg: '#f3f4f6' },
  no_show:         { dot: '#f59e0b', text: '#92400e', bg: '#fef3c7' },
  not_looking_currently: { dot: '#6b7280', text: '#374151', bg: '#f3f4f6' },
};

export const statusKey = (s) => (s || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');

export const getStatusConfig = (status) =>
  STATUS_COLORS[statusKey(status)] || { dot: LEAD_THEME.muted, text: LEAD_THEME.subtle, bg: LEAD_THEME.borderL };

// ─── Constants ──────────────────────────────────────────────────────────────
export const ALL_STAGES = ['all', 'new', 'unassigned', 'contacted', 'qualified', 'site_visit', 'negotiation', 'closure', 'junk', 'archived', 'deleted'];

/** Closure-stage leads are excluded from bulk list actions (assign, tags, notes, status, delete). */
export const isClosureStageLead = (lead) => {
  if (!lead) return false;
  const s = (lead.lead_stage ?? lead.stage ?? '').toString().toLowerCase();
  return s === 'closure';
};

export const DEFAULT_VISIBLE_COLUMNS = [
  'lead_id', 'name', 'email', 'mobile', 'created_at', 'last_contacted_at',
  'stage', 'status', 'source', 'sales_owner', 'next_followup', 'next_visit',
];

// ─── Date/time formatting ───────────────────────────────────────────────────
export const LEAD_DATETIME_FORMAT = 'MMM DD, YY HH:mm';

export const formatLeadDateTime = (d) => (d ? dayjs(d).format(LEAD_DATETIME_FORMAT) : '—');

export const formatLeadDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';

export const getDaysSince = (d) => {
  if (!d) return null;
  const diff = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff}d ago`;
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
  return `${Math.floor(diff / 30)}mo ago`;
};

// ─── Label helpers ─────────────────────────────────────────────────────────
export const formatStatusLabel = (status) =>
  status && status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Title-case for display (names, enums, snake_case). Hyphenated segments stay hyphenated (e.g. Jean-Luc, Follow-up).
 */
export const toTitleCase = (value) => {
  if (value == null || value === '') return '';
  const normalized = String(value).trim().replace(/_/g, ' ');
  if (!normalized) return '';
  return normalized
    .toLowerCase()
    .split(/\s+/)
    .map((word) =>
      word
        .split('-')
        .map((part) => (part.length ? part[0].toUpperCase() + part.slice(1) : part))
        .join('-'),
    )
    .join(' ');
};

/** Backend task item_type → display label (keep in sync with task UI). */
export const TASK_ITEM_TYPE_LABELS = {
  contact_call: 'Call',
  contact_meeting: 'Meeting',
  meeting: 'Meeting',
  lead_followup: 'Follow-up',
  payment_followup: 'Payment',
  document_collection: 'Document',
  contract_signing: 'Contract',
  send_proposal: 'Proposal',
  site_visit: 'Site Visit',
  property_inspection: 'Inspection',
  property_photoshoot: 'Photoshoot',
  property_valuation: 'Valuation',
  legal_verification: 'Legal',
  whatsapp_message: 'WhatsApp',
  task: 'Task',
  other: 'Other',
};

/**
 * Prefer API verbose label when present; else known map; else title-case raw item_type.
 */
export const getTaskItemTypeLabel = (itemType, itemTypeVerbose) => {
  const verbose = itemTypeVerbose != null && String(itemTypeVerbose).trim();
  if (verbose) return toTitleCase(String(itemTypeVerbose).trim());
  const key = String(itemType ?? '').toLowerCase().trim();
  if (TASK_ITEM_TYPE_LABELS[key]) return TASK_ITEM_TYPE_LABELS[key];
  return toTitleCase(String(itemType ?? '').replace(/_/g, ' ')) || 'Task';
};

export const TASK_PRIORITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export const getTaskPriorityLabel = (priority) => {
  const key = String(priority ?? '').toLowerCase().trim();
  if (TASK_PRIORITY_LABELS[key]) return TASK_PRIORITY_LABELS[key];
  return toTitleCase(String(priority ?? '').replace(/_/g, ' ')) || '—';
};

export const TASK_STATUS_DISPLAY_LABELS = {
  completed: 'Completed',
  cancelled: 'Cancelled',
  pending: 'Pending',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  update_pending: 'Update Pending',
  overdue: 'Overdue',
};

export const getTaskStatusLabel = (status) => {
  const key = String(status ?? '').toLowerCase().trim();
  if (TASK_STATUS_DISPLAY_LABELS[key]) return TASK_STATUS_DISPLAY_LABELS[key];
  return toTitleCase(String(status ?? '').replace(/_/g, ' ')) || '—';
};

// ─── Pill style getters (for use in any component) ──────────────────────────
export const getStagePillStyle = (stage) => {
  const cfg = getStageConfig(stage);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: cfg.bg,
    color: cfg.text,
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 9px 3px 7px',
    borderRadius: 99,
    whiteSpace: 'nowrap',
  };
};

export const getStatusPillStyle = (status) => {
  const cfg = getStatusConfig(status);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: cfg.bg,
    color: cfg.text,
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 9px 3px 7px',
    borderRadius: 99,
    whiteSpace: 'nowrap',
  };
};


// ── Activity Management Helpers ───────────────────────────────────────────
// ── Activity Management Helpers ───────────────────────────────────────────

const ICON_PATHS = {
  // Lead lifecycle
  lead_created:              `<polyline points="12,5 12,19"/><polyline points="5,12 12,5 19,12"/>`,
  lead_note_added:           `<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>`,
  lead_contact_updated:      `<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/><line x1="8" y1="13" x2="12" y2="13"/>`,
  lead_reactivated:          `<polyline points="1,4 1,10 7,10"/><path d="M3.51 15a9 9 0 1 0 .49-4"/>`,
  lead_archived:             `<polyline points="21,8 21,21 3,21 3,8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>`,
  lead_deleted:              `<polyline points="3,6 5,6 21,6"/><path d="M19,6l-1,14H6L5,6"/><path d="M10,11v6"/><path d="M14,11v6"/>`,
  lead_deleted_tasks:        `<polyline points="3,6 5,6 21,6"/><path d="M19,6l-1,14H6L5,6"/><line x1="10" y1="11" x2="14" y2="15"/><line x1="14" y1="11" x2="10" y2="15"/>`,

  // Status & Stage — real API keys
  lead_status_changed:       `<polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>`,
  lead_stage_changed:        `<path d="M5,12h14"/><path d="M12,5l7,7-7,7"/>`,
  lead_source_changed:       `<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12,2a15.3,15.3,0,0,1,4,10,15.3,15.3,0,0,1-4,10,15.3,15.3,0,0,1-4-10,15.3,15.3,0,0,1,4-10z"/>`,
  // Legacy keys kept for backward compat
  status_changed:            `<polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>`,
  stage_changed:             `<path d="M5,12h14"/><path d="M12,5l7,7-7,7"/>`,
  source_changed:            `<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12,2a15.3,15.3,0,0,1,4,10,15.3,15.3,0,0,1-4,10,15.3,15.3,0,0,1-4-10,15.3,15.3,0,0,1,4-10z"/>`,

  // Assignment
  assigned:                  `<path d="M17,21v-2a4,4,0,0,0-4-4H5a4,4,0,0,0-4,4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/>`,
  reassigned:                `<path d="M17,21v-2a4,4,0,0,0-4-4H5a4,4,0,0,0-4,4v2"/><circle cx="9" cy="7" r="4"/><path d="M23,21v-2a4,4,0,0,0-3-3.87"/><path d="M16,3.13a4,4,0,0,1,0,7.75"/>`,

  // Tasks — real API key
  task_created:              `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9,16 11,18 15,14"/>`,
  task_updated:              `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M18.5 14.5a2.121 2.121 0 0 1 3 3L12 27l-4 1 1-4 9.5-9.5z"/>`,
  task_completed:            `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="8,14 11,17 16,12"/>`,
  task_deleted:              `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="14" x2="15" y2="20"/><line x1="15" y1="14" x2="9" y2="20"/>`,

  // Communication
  call_made:                 `<path d="M22,16.92v3a2,2,0,0,1-2.18,2,19.79,19.79,0,0,1-8.63-3.07A19.5,19.5,0,0,1,4.69,12,19.79,19.79,0,0,1,1.61,3.4,2,2,0,0,1,3.6,1.22h3a2,2,0,0,1,2,1.72,12.84,12.84,0,0,0,.7,2.81,2,2,0,0,1-.45,2.11L7.91,8.8a16,16,0,0,0,6.29,6.29l1.16-1.16a2,2,0,0,1,2.11-.45,12.84,12.84,0,0,0,2.81.7A2,2,0,0,1,22,16.92z"/><line x1="18" y1="2" x2="22" y2="6"/><line x1="22" y1="2" x2="18" y2="6"/>`,
  call_received:             `<path d="M22,16.92v3a2,2,0,0,1-2.18,2,19.79,19.79,0,0,1-8.63-3.07A19.5,19.5,0,0,1,4.69,12,19.79,19.79,0,0,1,1.61,3.4,2,2,0,0,1,3.6,1.22h3a2,2,0,0,1,2,1.72,12.84,12.84,0,0,0,.7,2.81,2,2,0,0,1-.45,2.11L7.91,8.8a16,16,0,0,0,6.29,6.29l1.16-1.16a2,2,0,0,1,2.11-.45,12.84,12.84,0,0,0,2.81.7A2,2,0,0,1,22,16.92z"/><polyline points="18,2 22,6 18,6"/>`,
  email_sent:                `<path d="M4,4h16c1.1,0,2,.9,2,2v12c0,1.1-.9,2-2,2H4c-1.1,0-2-.9-2-2V6c0-1.1,.9-2,2-2z"/><polyline points="22,6 12,13 2,6"/>`,
  message_sent:              `<path d="M21,15a2,2,0,0,1-2,2H7l-4,4V5a2,2,0,0,1,2-2H19a2,2,0,0,1,2,2z"/>`,
  meeting_scheduled:         `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>`,
  meeting_completed:         `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9,16 11,18 15,14"/>`,

  // Property
  property_shared:           `<path d="M3,9l9-7,9,7v11a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/><line x1="18" y1="8" x2="22" y2="8"/>`,
  property_shortlisted:      `<path d="M3,9l9-7,9,7v11a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/><polyline points="9,14 11,16 15,12"/>`,
  site_visit_scheduled:      `<circle cx="12" cy="10" r="3"/><path d="M12,2a8,8,0,0,1,8,8c0,5.5-8,13-8,13S4,15.5,4,10A8,8,0,0,1,12,2z"/>`,
  site_visit_completed:      `<circle cx="12" cy="10" r="3"/><path d="M12,2a8,8,0,0,1,8,8c0,5.5-8,13-8,13S4,15.5,4,10A8,8,0,0,1,12,2z"/><polyline points="9,10 11,12 15,8"/>`,
  site_visit_cancelled:      `<circle cx="12" cy="10" r="3"/><path d="M12,2a8,8,0,0,1,8,8c0,5.5-8,13-8,13S4,15.5,4,10A8,8,0,0,1,12,2z"/><line x1="10" y1="8" x2="14" y2="12"/><line x1="14" y1="8" x2="10" y2="12"/>`,

  // Follow-ups
  followup_scheduled:        `<circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>`,
  followup_completed:        `<circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/><polyline points="9,12 11,14 15,10"/>`,
  followup_missed:           `<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>`,

  // Requirements & Preferences
  requirements_updated:      `<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>`,
  preferences_updated:       `<circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41"/><path d="M4.93 4.93l1.41 1.41"/><path d="M19.07 19.07l-1.41-1.41"/><path d="M4.93 19.07l1.41-1.41"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/>`,
  additional_preferences_updated: `<circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="19" y1="3" x2="23" y2="3"/>`,
  property_preferences_updated: `<path d="M3,9l9-7,9,7v11a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/><circle cx="19" cy="19" r="3"/><path d="M17.5 17.5l1 1 2-2"/>`,
  budget_updated:            `<line x1="12" y1="1" x2="12" y2="23"/><path d="M17,5H9.5a3.5,3.5,0,0,0,0,7h5a3.5,3.5,0,0,1,0,7H6"/>`,

  // Negotiation & Closure
  price_quoted:              `<line x1="12" y1="1" x2="12" y2="23"/><path d="M17,5H9.5a3.5,3.5,0,0,0,0,7h5a3.5,3.5,0,0,1,0,7H6"/>`,
  offer_made:                `<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>`,
  offer_accepted:            `<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>`,
  offer_rejected:            `<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>`,
  deal_closed:               `<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>`,
  contract_signed:           `<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>`,

  // Lost / Junk
  lead_lost:                 `<circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>`,
  lead_dropped:              `<polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/>`,
  marked_junk:               `<polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>`,

  // Notes & Documents
  note_added:                `<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>`,
  note_updated:              `<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/><line x1="8" y1="13" x2="12" y2="13"/>`,
  note_deleted:              `<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>`,
  document_uploaded:         `<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>`,
  document_shared:           `<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>`,
  tags_added:                `<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/><line x1="11" y1="4" x2="4" y2="11"/>`,
  tags_updated:              `<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/><path d="M18.5 14.5a2.121 2.121 0 0 1 3 3L12 27l-4 1 1-4 9.5-9.5z"/>`,
  preference_updated:        `<circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41"/><path d="M4.93 4.93l1.41 1.41"/><path d="M19.07 19.07l-1.41-1.41"/><path d="M4.93 19.07l1.41-1.41"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/>`,
  feedback_received:         `<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="9" y1="10" x2="15" y2="10"/>`,

  // Channel Partner
  channel_partner_created:   `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/>`,
  channel_partner_updated:   `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 11l-3 3-3-3"/>`,
  channel_partner_assigned:  `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/>`,
  channel_partner_removed:   `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="11" x2="23" y2="17"/><line x1="23" y1="11" x2="17" y2="17"/>`,
  channel_partner_deleted:   `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="8" x2="23" y2="8"/>`,

  // Import / system
  csv_import:                `<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>`,
  manual:                    `<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>`,
  other:                     `<circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>`,
  system:                    `<circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>`,

  // Lead management
  lead_management:           `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 11l-3 3-3-3M20 14V8"/>`,
  lead_not_interested:       `<circle cx="12" cy="12" r="10"/><path d="M8 15s1.5-2 4-2 4 2 4 2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>`,
  lead_qualified:            `<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>`,
  lead_restored:             `<polyline points="1,4 1,10 7,10"/><path d="M3.51 15a9 9 0 1 0 .49-4"/>`,

  // Call (generic)
  call:                      `<path d="M22,16.92v3a2,2,0,0,1-2.18,2,19.79,19.79,0,0,1-8.63-3.07A19.5,19.5,0,0,1,4.69,12,19.79,19.79,0,0,1,1.61,3.4,2,2,0,0,1,3.6,1.22h3a2,2,0,0,1,2,1.72,12.84,12.84,0,0,0,.7,2.81,2,2,0,0,1-.45,2.11L7.91,8.8a16,16,0,0,0,6.29,6.29l1.16-1.16a2,2,0,0,1,2.11-.45,12.84,12.84,0,0,0,2.81.7A2,2,0,0,1,22,16.92z"/>`,

  // Site visit (generic)
  site_visit:                `<circle cx="12" cy="10" r="3"/><path d="M12,2a8,8,0,0,1,8,8c0,5.5-8,13-8,13S4,15.5,4,10A8,8,0,0,1,12,2z"/>`,

  // Task variants
  task_completion:           `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="8,14 11,17 16,12"/>`,
  task_management:           `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="13" y2="18"/>`,
  task_note:                 `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M13 14H8M11 18H8"/><path d="M16.5 14.5a2 2 0 0 1 2.5 2.5L15 21l-3 1 1-3 3.5-4.5z"/>`,
  task_update:               `<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="1,4 1,10 7,10"/><path d="M3.51 18a7 7 0 0 0 9.49.49"/>`,

  // WhatsApp
  whatsapp:                  `<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>`,
};

const DEFAULT_ICON_PATH = `<path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1" ry="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/>`;

export const ActivityIcon = ({ type, size = 14 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    dangerouslySetInnerHTML={{ __html: ICON_PATHS[type] || DEFAULT_ICON_PATH }}
  />
);

// ── Color palette ─────────────────────────────────────────────────────────
const PALETTE = {
  green:  { color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
  blue:   { color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
  sky:    { color: '#0284C7', bg: '#F0F9FF', border: '#BAE6FD' },
  purple: { color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  teal:   { color: '#0F766E', bg: '#F0FDFA', border: '#99F6E4' },
  amber:  { color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  indigo: { color: '#4F46E5', bg: '#EEF2FF', border: '#C7D2FE' },
  orange: { color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
  red:    { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  slate:  { color: '#475569', bg: '#F8FAFC', border: '#E2E8F0' },
  navy:   { color: '#0369A1', bg: '#F0F9FF', border: '#BAE6FD' },
  gray:   { color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' },
  violet: { color: '#6D28D9', bg: '#F5F3FF', border: '#C4B5FD' },
};

// ── Full activity config map ──────────────────────────────────────────────
const ACTIVITY_MAP = {
  // Lead lifecycle
  lead_created:                    { palette: 'green',  label: 'Lead Created' },
  lead_note_added:                 { palette: 'slate',  label: 'Note Added' },
  lead_contact_updated:            { palette: 'blue',   label: 'Contact Updated' },
  lead_reactivated:                { palette: 'green',  label: 'Lead Reactivated' },
  lead_archived:                   { palette: 'gray',   label: 'Lead Archived' },
  lead_deleted:                    { palette: 'red',    label: 'Lead Deleted' },
  lead_deleted_tasks:              { palette: 'red',    label: 'Tasks Deleted' },

  // Status & Stage — real API keys (prefixed with lead_)
  lead_status_changed:             { palette: 'sky',    label: 'Status Changed' },
  lead_stage_changed:              { palette: 'indigo', label: 'Stage Changed' },
  lead_source_changed:             { palette: 'slate',  label: 'Source Changed' },
  // Legacy / alternate keys
  status_changed:                  { palette: 'sky',    label: 'Status Changed' },
  stage_changed:                   { palette: 'indigo', label: 'Stage Changed' },
  source_changed:                  { palette: 'slate',  label: 'Source Changed' },

  // Assignment
  assigned:                        { palette: 'purple', label: 'Lead Assigned' },
  reassigned:                      { palette: 'purple', label: 'Lead Reassigned' },

  // Tasks — real API key
  task_created:                    { palette: 'amber',  label: 'Task Created' },
  task_updated:                    { palette: 'amber',  label: 'Task Updated' },
  task_completed:                  { palette: 'green',  label: 'Task Completed' },
  task_deleted:                    { palette: 'red',    label: 'Task Deleted' },

  // Communication
  call_made:                       { palette: 'green',  label: 'Call Made' },
  call_received:                   { palette: 'green',  label: 'Call Received' },
  email_sent:                      { palette: 'blue',   label: 'Email Sent' },
  message_sent:                    { palette: 'blue',   label: 'Message Sent' },
  meeting_scheduled:               { palette: 'purple', label: 'Meeting Scheduled' },
  meeting_completed:               { palette: 'green',  label: 'Meeting Completed' },

  // Property
  property_shared:                 { palette: 'teal',   label: 'Property Shared' },
  property_shortlisted:            { palette: 'teal',   label: 'Property Shortlisted' },
  site_visit_scheduled:            { palette: 'teal',   label: 'Site Visit Scheduled' },
  site_visit_completed:            { palette: 'teal',   label: 'Site Visit Completed' },
  site_visit_cancelled:            { palette: 'red',    label: 'Site Visit Cancelled' },

  // Follow-ups
  followup_scheduled:              { palette: 'amber',  label: 'Follow-up Scheduled' },
  followup_completed:              { palette: 'green',  label: 'Follow-up Completed' },
  followup_missed:                 { palette: 'red',    label: 'Follow-up Missed' },

  // Requirements & Preferences
  requirements_updated:            { palette: 'indigo', label: 'Requirements Updated' },
  preference_updated:              { palette: 'indigo', label: 'Preference Updated' },
  preferences_updated:             { palette: 'indigo', label: 'Preferences Updated' },
  additional_preferences_updated:  { palette: 'indigo', label: 'Additional Preferences' },
  property_preferences_updated:    { palette: 'indigo', label: 'Property Preferences' },
  budget_updated:                  { palette: 'amber',  label: 'Budget Updated' },

  // Negotiation & Closure
  price_quoted:                    { palette: 'orange', label: 'Price Quoted' },
  offer_made:                      { palette: 'orange', label: 'Offer Made' },
  offer_accepted:                  { palette: 'green',  label: 'Offer Accepted' },
  offer_rejected:                  { palette: 'red',    label: 'Offer Rejected' },
  deal_closed:                     { palette: 'green',  label: 'Deal Closed' },
  contract_signed:                 { palette: 'green',  label: 'Contract Signed' },

  // Lost / Junk
  lead_lost:                       { palette: 'red',    label: 'Lead Lost' },
  lead_dropped:                    { palette: 'red',    label: 'Lead Dropped' },
  marked_junk:                     { palette: 'red',    label: 'Marked as Junk' },

  // Tags
  tags_updated:                    { palette: 'indigo', label: 'Tags Updated' },

  // Notes & Documents
  note_added:                      { palette: 'slate',  label: 'Note Added' },
  lead_note_added:                 { palette: 'slate',  label: 'Note Added' },
  note_updated:                    { palette: 'slate',  label: 'Note Updated' },
  note_deleted:                    { palette: 'red',    label: 'Note Deleted' },
  document_uploaded:               { palette: 'slate',  label: 'Document Uploaded' },
  document_shared:                 { palette: 'slate',  label: 'Document Shared' },
  tags_added:                      { palette: 'indigo', label: 'Tags Added' },
  feedback_received:               { palette: 'amber',  label: 'Feedback Received' },

  // Channel Partner
  channel_partner_created:         { palette: 'navy',   label: 'Partner Created' },
  channel_partner_updated:         { palette: 'navy',   label: 'Partner Updated' },
  channel_partner_assigned:        { palette: 'navy',   label: 'Partner Assigned' },
  channel_partner_removed:         { palette: 'red',    label: 'Partner Removed' },
  channel_partner_deleted:         { palette: 'red',    label: 'Partner Deleted' },

  // Import / system activity types
  csv_import:                      { palette: 'slate',  label: 'CSV Import' },
  manual:                          { palette: 'slate',  label: 'Manual Activity' },
  other:                           { palette: 'gray',   label: 'Activity' },
  system:                          { palette: 'gray',   label: 'System Activity' },

  // Lead management
  lead_management:                 { palette: 'blue',   label: 'Lead Management' },
  lead_not_interested:             { palette: 'red',    label: 'Not Interested' },
  lead_qualified:                  { palette: 'green',  label: 'Lead Qualified' },
  lead_restored:                   { palette: 'green',  label: 'Lead Restored' },

  // Call (generic — activity_type level)
  call:                            { palette: 'green',  label: 'Call' },

  // Site visit (generic)
  site_visit:                      { palette: 'teal',   label: 'Site Visit' },

  // Task variants
  task_completion:                 { palette: 'green',  label: 'Task Completed' },
  task_management:                 { palette: 'amber',  label: 'Task Management' },
  task_note:                       { palette: 'slate',  label: 'Task Note' },
  task_update:                     { palette: 'amber',  label: 'Task Updated' },

  // WhatsApp
  whatsapp:                        { palette: 'green',  label: 'WhatsApp' },
};

const DEFAULT_CONFIG = { palette: 'gray', label: 'Activity' };

// ── Public API ────────────────────────────────────────────────────────────

export const getActivityConfig = (activityType) => {
  const entry  = ACTIVITY_MAP[activityType] || DEFAULT_CONFIG;
  const colors = PALETTE[entry.palette];
  return {
    label:  entry.label,
    color:  colors.color,
    bg:     colors.bg,
    border: colors.border,
  };
};

export const getActivityBackground = (activityType) => {
  const { bg } = getActivityConfig(activityType);
  return bg;
};

export const getActivityBorderColor = (activityType) => {
  const { border } = getActivityConfig(activityType);
  return border;
};

/**
 * Maps every action key → category.
 * Handles both legacy keys (status_changed) and real API keys (lead_status_changed).
 */
export const getActivityCategory = (activityType = '') => {
  const t = activityType.toLowerCase();

  // Tasks — explicit matches first to avoid partial collisions
  if (['task_created','task_updated','task_completed','task_deleted',
       'task_completion','task_management','task_note','task_update'].includes(t))
    return 'followup';

  // WhatsApp / call (generic)
  if (t === 'whatsapp') return 'communication';
  if (t === 'call') return 'communication';

  // Site visit (generic)
  if (t === 'site_visit') return 'property';

  // Channel partner — check before 'assigned' to avoid misrouting
  if (t.includes('channel_partner')) return 'channel';

  // Tags & notes
  if (t === 'tags_updated' || t === 'tags_added') return 'notes';
  if (t.includes('note') || t.includes('document') || t.includes('feedback')) return 'notes';

  // Communication
  if (t.includes('call') || t.includes('email') || t.includes('message') || t.includes('meeting'))
    return 'communication';

  // Property
  if (t.includes('site_visit') || t.includes('property_shared') || t.includes('property_shortlisted'))
    return 'property';

  // Follow-ups
  if (t.includes('followup'))                    return 'followup';

  // Status / stage / source
  if (t.includes('status') || t.includes('stage') || t.includes('source'))
    return 'status';

  // Assignment
  if (t === 'assigned' || t === 'reassigned' || t.includes('assigned'))
    return 'assignment';

  // Requirements & preferences
  if (t === 'preference_updated' ||
      t.includes('requirements') || t.includes('preferences') || t.includes('budget'))
    return 'requirements';

  // Negotiation
  if (t.includes('price') || t.includes('offer') || t.includes('deal') || t.includes('contract'))
    return 'negotiation';

  // Lost / junk / not interested
  if (t.includes('lost') || t.includes('dropped') || t.includes('junk') || t === 'lead_not_interested')
    return 'lost';

  // Everything else → lead bucket
  // (lead_created, lead_deleted, lead_qualified, lead_restored, lead_management,
  //  csv_import, manual, other, system, etc.)
  return 'lead';
};